import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { track } from '@amplitude/analytics-browser'
import {
  SectionHeading,
  TextField,
  Toggle,
  colors,
} from 'src/components/app-ui'
import { Badge } from 'src/components/user-access/components'
import UpgradePopover from 'src/components/upgrade-popover/upgrade-popover'
import {
  useCourse,
  useCourseSetting,
  useCourseSettings,
} from 'src/hooks/useCourseSettings'
import { languages as courseLanguages } from 'src/model/languages'
import { PatchContent, PatchData } from 'src/model/patch'
import {
  coursePatchAction,
  courseSettingPatchById,
} from 'src/reducers/course/action'
import UserType from 'src/model/user-types'
import useGeneralSettings from './use-general-settings'
import CourseUrlField from './course-url-field'
import TtsSettings, { readTextToSpeech } from './tts-settings'

// Plans that include the accessibility widget and the advanced voices.
const ACCESSIBILITY_PLANS = [
  'Elite',
  'AppSumo (Tier 5)',
  'AppSumo (Tier 6)',
  'AppSumo (Tier 7)',
  'AppSumo (Tier 8)',
  'AppSumo (Tier 9)',
  'Super Admin',
  'Plus',
  'Pro v2',
  'Launch',
  'Essentials',
  'Growth',
  'Scale',
  'Enterprise',
]

const UPGRADE_COPY = {
  pro: 'Upgrade to Plus plan to access this feature.',
  accessibility: 'Upgrade to Plus plan to access accessibility features.',
}

const useStyles = makeStyles({
  field: {
    maxWidth: 640,
    fontSize: '14px',
    fontWeight: 500,
    color: colors.navy,
  },
  language: {
    maxWidth: 640,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  languageLabel: {
    fontSize: '14px',
    fontWeight: 500,
  },
  languageSelectWrap: {
    position: 'relative',
    display: 'block',
  },
  languageSelect: {
    width: '100%',
    minWidth: 0,
    padding: '10px 36px 10px 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    outline: 'none',
    background: '#FFFFFF',
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: colors.navy,
  },
  languageChevron: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.muted,
    pointerEvents: 'none',
  },
  help: {
    fontSize: '12px',
    color: colors.muted,
  },
  link: {
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    color: colors.primary,
  },
  rows: {
    maxWidth: 720,
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '14px 0',
    borderBottom: '1px solid #F1F3F5',
  },
  rowHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  rowTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '15px',
    color: colors.navy,
  },
  rowDesc: {
    fontSize: '14px',
    color: colors.muted,
  },
  inherited: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '6px',
    background: colors.hover,
    fontSize: '13px',
    color: colors.muted,
    whiteSpace: 'nowrap',
  },
  scripts: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    paddingTop: 8,
  },
  scriptLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  textarea: {
    padding: '10px 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: '12px',
    color: colors.navy,
  },
  planPill: {
    padding: '0 8px',
    borderRadius: '5px',
    background: '#015ffb14',
    fontSize: '11px',
    fontWeight: 500,
    color: colors.primary,
  },
})

/** A settings row: title (+ badge) with a control on the right. */
const Row = ({ title, badge, control, description, children }) => {
  const classes = useStyles()
  return (
    <div className={classes.row}>
      <div className={classes.rowHead}>
        <span className={classes.rowTitle}>
          {title}
          {badge}
        </span>
        {control}
      </div>
      {description && <div className={classes.rowDesc}>{description}</div>}
      {children}
    </div>
  )
}

const CourseTitleField = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const course = useSelector(state => state.course.course)
  const [title, setTitle] = useState(course.name)

  useEffect(() => setTitle(course.name), [course.name])

  const save = () => {
    const next = title.trim()
    if (!next || next === course.name) return
    const patch = new PatchData(null, course.id)
    patch.addContent(
      new PatchContent(next, 'name', PatchData.OPERATION_REPLACE),
    )
    dispatch(coursePatchAction(patch, () => {}))
  }

  return (
    <TextField
      label="Course title"
      className={classes.field}
      value={title}
      onChange={event => setTitle(event.target.value)}
      onBlur={save}
      onKeyDown={event => event.key === 'Enter' && event.target.blur()}
    />
  )
}

// The language list has a few entries per code (English variants); list each code once.
const LANGUAGE_OPTIONS = courseLanguages.filter(
  (language, index) =>
    courseLanguages.findIndex(
      item => item.countryCode === language.countryCode,
    ) === index,
)

/**
 * The course language. Its built-in texts (buttons, messages) are edited per
 * language for the whole workspace in Workspace > Language.
 */
const CourseLanguageField = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const course = useCourse()
  const { setting } = useCourseSetting('language')
  const { value } = useCourseSettings('language')
  const saved = value?.selected || 'en'
  const [code, setCode] = useState(saved)

  useEffect(() => setCode(saved), [saved])

  const change = event => {
    const next = event.target.value
    setCode(next)
    dispatch(
      courseSettingPatchById(
        course.id,
        setting?.id || 0,
        { value: JSON.stringify({ selected: next }) },
        () => enqueueSnackbar('Saved', { variant: 'success' }),
      ),
    )
  }

  const editTexts = () => history.push(`/workspace/language?lang=${code}`)

  return (
    <label className={classes.language}>
      <span className={classes.languageLabel}>Course language</span>
      <span className={classes.languageSelectWrap}>
        <select
          className={classes.languageSelect}
          value={code}
          onChange={change}
        >
          {LANGUAGE_OPTIONS.map(language => (
            <option key={language.countryCode} value={language.countryCode}>
              {language.name}
            </option>
          ))}
        </select>
        <Icon
          icon="tabler:chevron-down"
          width={16}
          height={16}
          className={classes.languageChevron}
        />
      </span>
      <span className={classes.help}>
        Buttons and messages in this course use this language.{' '}
        <button type="button" className={classes.link} onClick={editTexts}>
          Edit interface text
        </button>
      </span>
    </label>
  )
}

/**
 * Course > Settings > General. Every change saves immediately to the
 * course's `general` setting (no Save button).
 */
const GeneralSection = () => {
  const classes = useStyles()
  const course = useSelector(state => state.course.course)
  const [values, save] = useGeneralSettings()
  const [upgrade, setUpgrade] = useState(null)

  const plan = UserType[window.CONSTANTS.USER_ROLE_ID]
  const isPremium = Number(window.CONSTANTS.USER_ROLE_ID) !== 1
  const hasScriptAccess = plan !== 'Basic' && !UserType.isTrialEquivalent(plan)
  const hasAccessibility =
    ACCESSIBILITY_PLANS.includes(plan) || UserType.isTrialEquivalent(plan)
  const isInPath = Boolean(course.courseCollectionId)

  const [scriptsOpen, setScriptsOpen] = useState(
    Boolean(values.headerScript || values.bodyScript),
  )
  useEffect(() => {
    if (values.headerScript || values.bodyScript) setScriptsOpen(true)
  }, [values.headerScript, values.bodyScript])

  const tts = readTextToSpeech(values)
  const saveTts = next =>
    save({
      enablePageReader: next.value,
      pageReaderSettings: { textToSpeech: next },
    })

  const askUpgrade = (event, kind) =>
    setUpgrade({ anchor: event.currentTarget, kind })

  const toggleScripts = (next, event) => {
    if (!hasScriptAccess) {
      track('limit_warning_upgrade', { from: 'course_script_limit_upgrade' })
      window.location.href = '/pricing'
      return
    }
    setScriptsOpen(next)
  }

  return (
    <>
      <SectionHeading
        title="General settings"
        description="Customize how this course behaves for learners."
      />
      <CourseTitleField />
      <CourseUrlField />
      <CourseLanguageField />

      <div className={classes.rows}>
        <Row
          title="Notify by email when someone completes"
          badge={<Badge type={4} />}
          control={
            <Toggle
              checked={Boolean(values.sendEmailOnFinish)}
              onChange={(next, event) =>
                isPremium
                  ? save({ sendEmailOnFinish: next })
                  : askUpgrade(event, 'pro')
              }
            />
          }
        />

        <Row
          title="Add header and body scripts"
          badge={
            !hasScriptAccess && <span className={classes.planPill}>Plus</span>
          }
          control={<Toggle checked={scriptsOpen} onChange={toggleScripts} />}
        >
          {scriptsOpen && hasScriptAccess && (
            <div className={classes.scripts}>
              <label className={classes.scriptLabel}>
                Header scripts
                <textarea
                  rows={4}
                  className={classes.textarea}
                  placeholder="<script>…</script>"
                  defaultValue={values.headerScript || ''}
                  onBlur={event =>
                    event.target.value !== (values.headerScript || '') &&
                    save({ headerScript: event.target.value })
                  }
                />
              </label>
              <label className={classes.scriptLabel}>
                Body scripts
                <textarea
                  rows={4}
                  className={classes.textarea}
                  placeholder="<script>…</script>"
                  defaultValue={values.bodyScript || ''}
                  onBlur={event =>
                    event.target.value !== (values.bodyScript || '') &&
                    save({ bodyScript: event.target.value })
                  }
                />
              </label>
            </div>
          )}
        </Row>

        <Row
          title="Show Accessibility Widget"
          badge={!isInPath && <Badge type={6} />}
          description={
            !isInPath &&
            'Show a floating accessibility menu to assist learners with disabilities.'
          }
          control={
            isInPath ? (
              // Courses in a learning path follow the path's setting.
              <span className={classes.inherited}>
                <Icon icon="tabler:route" width={14} height={14} />
                Set by the learning path.
              </span>
            ) : (
              <Toggle
                checked={Boolean(values.enableAccessibilityWidget)}
                onChange={(next, event) =>
                  hasAccessibility
                    ? save({ enableAccessibilityWidget: next })
                    : askUpgrade(event, 'accessibility')
                }
              />
            )
          }
        />

        <Row
          title="Page Reader (Text to Speech)"
          control={
            <Toggle
              checked={tts.value}
              onChange={next => saveTts({ ...tts, value: next })}
            />
          }
        />
        {tts.value && (
          <TtsSettings
            tts={tts}
            onChange={saveTts}
            canUseAdvanced={hasAccessibility}
            onUpgrade={event => askUpgrade(event, 'accessibility')}
          />
        )}
      </div>

      <UpgradePopover
        id="course-general-upgrade"
        anchorEl={upgrade?.anchor}
        open={Boolean(upgrade)}
        handleClose={() => setUpgrade(null)}
        header="Upgrade to Plus"
        content={UPGRADE_COPY[upgrade?.kind] || UPGRADE_COPY.pro}
      />
    </>
  )
}

export default GeneralSection
